import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import {Avatar, Box, Fade, Menu, MenuItem} from "@mui/material";
import type {Theme} from "@mui/material/styles";
import {MyAccountService} from "crqlar-react-common/dist/services/identity/myAccount.service";
import {getIconForLanguage, getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import type {MouseEvent, ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {Link as RouterLink} from "react-router-dom";
import {useMe} from "../../common/useMe";
import {
    SIDEBAR_BUTTON_BORDER,
    SIDEBAR_BUTTON_BORDER_RADIUS,
    SIDEBAR_CONTENT_BORDER_RADIUS,
} from "./sidebarLayout";
import {getUserAvatar} from "./userMenuAvatar";

const LANGUAGE_OPTIONS = ["en", "de", "it"] as const;
type DashboardLanguage = typeof LANGUAGE_OPTIONS[number];

interface ActionRowBaseProps {
    children: ReactNode;
    danger?: boolean;
    id: string;
}

type ActionRowProps = ActionRowBaseProps & (
    | {onAction?: () => void; to: string}
    | {onAction: () => void; to?: never}
);

interface UserMenuPopoverContentProps {
    languageAnchorEl: HTMLElement | null;
    onClose: () => void;
    onCloseLanguageMenu: (event?: unknown) => void;
    onChangePassword: () => void;
    onLogout: () => void;
    onOpenLanguageMenu: (event: MouseEvent<HTMLElement>) => void;
}

const ActionRow = ({children, danger = false, id, ...props}: ActionRowProps) => {
    const actionRowSx = (theme: Theme) => ({
        width: "100%",
        minHeight: 44,
        px: 1.25,
        py: 1,
        border: SIDEBAR_BUTTON_BORDER,
        borderColor: "transparent",
        borderRadius: SIDEBAR_BUTTON_BORDER_RADIUS,
        backgroundColor: "transparent",
        color: danger ? theme.palette.error.main : theme.palette.text.primary,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        cursor: "pointer",
        font: "inherit",
        textAlign: "left",
        textDecoration: "none",
        boxSizing: "border-box",
        "&:hover": {
            backgroundColor: danger ? theme.palette.error.light : theme.palette.action.hover,
            color: danger ? theme.palette.error.contrastText : undefined,
        },
        "&:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
        },
    });

    if (props.to) {
        return (
            <Box
                component={RouterLink}
                id={id}
                to={props.to}
                onClick={props.onAction}
                sx={actionRowSx}
            >
                {children}
            </Box>
        );
    }

    return (
        <Box component="button" id={id} type="button" onClick={props.onAction} sx={actionRowSx}>
            {children}
        </Box>
    );
};

const normalizeLanguage = (language?: string): DashboardLanguage => {
    const normalized = language?.toLowerCase().split(/[-_]/)[0];
    return LANGUAGE_OPTIONS.find(option => option === normalized) ?? "en";
};

export function UserMenuPopoverContent({
    languageAnchorEl,
    onClose,
    onCloseLanguageMenu,
    onChangePassword,
    onLogout,
    onOpenLanguageMenu,
}: UserMenuPopoverContentProps) {
    const {t, i18n} = useTranslation();
    const {user} = useMe();
    const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
    const languageMenuOpen = Boolean(languageAnchorEl);
    const {
        avatarColor,
        avatarInitials,
        avatarSrc,
        userName,
    } = getUserAvatar(user);
    const userType = user?.userType ? t(`User Type ${user.userType}`) : "-";

    const handleChangeLanguage = (nextLanguage: DashboardLanguage, event: MouseEvent<HTMLElement>) => {
        void i18n.changeLanguage(nextLanguage);
        void MyAccountService.changeLanguage(nextLanguage);
        onCloseLanguageMenu(event);
    };

    return (
        <>
            <Box sx={{p: 1.5}}>
                <Box sx={{display: "flex", gap: 1.5, alignItems: "center", minWidth: 0, px: 1, py: 1}}>
                    <Avatar
                        src={avatarSrc}
                        sx={{
                            width: 48,
                            height: 48,
                            fontSize: 17,
                            fontWeight: 700,
                            bgcolor: avatarColor,
                            color: "#fff",
                            flexShrink: 0,
                        }}
                    >
                        {avatarInitials}
                    </Avatar>

                    <Box sx={{minWidth: 0, flex: 1}}>
                        <Box
                            sx={{
                                fontSize: 15,
                                fontWeight: 700,
                                lineHeight: 1.2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {userName}
                        </Box>
                        <Box
                            sx={{
                                mt: 0.25,
                                fontSize: 13,
                                color: "text.secondary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {user?.email || "-"}
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        mt: 1,
                        mx: 1,
                        px: 1.25,
                        py: 1,
                        borderRadius: SIDEBAR_CONTENT_BORDER_RADIUS,
                        bgcolor: "rgba(15, 23, 42, 0.035)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1.25,
                    }}
                >
                    <Box sx={{display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1}}>
                        <AdminPanelSettingsIcon sx={{fontSize: 19, flexShrink: 0}}/>
                        <Box
                            sx={{
                                minWidth: 0,
                                fontSize: 14,
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {userType}
                        </Box>
                    </Box>

                    <Box
                        component="button"
                        id="dashboard-user-language-button"
                        type="button"
                        aria-controls={languageMenuOpen ? "dashboard-user-language-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={languageMenuOpen}
                        onClick={onOpenLanguageMenu}
                        sx={{
                            height: 34,
                            maxWidth: 128,
                            px: 1,
                            border: SIDEBAR_BUTTON_BORDER,
                            borderColor: "divider",
                            borderRadius: SIDEBAR_BUTTON_BORDER_RADIUS,
                            bgcolor: "background.paper",
                            color: "text.primary",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            cursor: "pointer",
                            font: "inherit",
                            flexShrink: 0,
                            "&:hover": {bgcolor: "action.hover"},
                            "&:focus-visible": {
                                outline: theme => `2px solid ${theme.palette.primary.main}`,
                                outlineOffset: 2,
                            },
                        }}
                    >
                        <img
                            src={getIconForLanguage(language)}
                            alt=""
                            style={{width: 18, height: 18, borderRadius: "50%", objectFit: "cover"}}
                        />
                        <Box
                            sx={{
                                minWidth: 0,
                                fontSize: 13,
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {getNameForLanguage(language, true, true)}
                        </Box>
                        <KeyboardArrowDownIcon sx={{fontSize: 18, flexShrink: 0}}/>
                    </Box>
                </Box>

                <Box sx={{height: 1, my: 1.25, bgcolor: "divider"}}/>

                <Box sx={{px: 0.5}}>
                    <ActionRow id="dashboard-user-menu-account-button" to="/identity/myAccount" onAction={onClose}>
                        <PersonOutlinedIcon sx={{fontSize: 20}}/>
                        <Box sx={{fontSize: 14, fontWeight: 500}}>{t("My account")}</Box>
                    </ActionRow>
                    <ActionRow id="dashboard-user-menu-password-button" onAction={onChangePassword}>
                        <LockOutlinedIcon sx={{fontSize: 20}}/>
                        <Box sx={{fontSize: 14, fontWeight: 500}}>{t("Change password")}</Box>
                    </ActionRow>
                </Box>

                <Box sx={{height: 1, my: 0.5, bgcolor: "divider"}}/>

                <Box sx={{px: 0.5}}>
                    <ActionRow id="dashboard-user-menu-logout-button" danger onAction={onLogout}>
                        <LogoutOutlinedIcon sx={{fontSize: 20}}/>
                        <Box sx={{fontSize: 14, fontWeight: 600}}>{t("Logout")}</Box>
                    </ActionRow>
                </Box>
            </Box>

            <Menu
                id="dashboard-user-language-menu"
                anchorEl={languageAnchorEl}
                open={languageMenuOpen}
                onClose={onCloseLanguageMenu}
                TransitionComponent={Fade}
                transitionDuration={100}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
                PaperProps={{
                    sx: {
                        minWidth: 168,
                        mt: 0.75,
                        borderRadius: SIDEBAR_CONTENT_BORDER_RADIUS,
                        boxShadow: "0 12px 32px rgba(15, 23, 42, 0.16)",
                    },
                }}
            >
                {LANGUAGE_OPTIONS.map(option => (
                    <MenuItem
                        id={`dashboard-user-language-option-${option}`}
                        key={option}
                        onClick={event => handleChangeLanguage(option, event)}
                        sx={{minHeight: 38, gap: 1, borderRadius: SIDEBAR_BUTTON_BORDER_RADIUS}}
                    >
                        <img
                            src={getIconForLanguage(option)}
                            alt=""
                            style={{width: 18, height: 18, borderRadius: "50%", objectFit: "cover"}}
                        />
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: 14,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {getNameForLanguage(option, true, true)}
                        </Box>
                        {language === option && <CheckIcon sx={{fontSize: 18, color: "primary.main"}}/>}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
